import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ContaPagar } from './contapagar';
import { ContaPagarService } from 'src/app/service/contaPagar.service';
import { ClientService } from 'src/app/service/client.service';
import { FornecedorService } from 'src/app/service/fornecedor.service';
import { ColaboradorService } from 'src/app/service/colaborador.service';
import { SegmentoService } from 'src/app/service/segmento.service';
import { Segmento } from '../segmento/segmento';
import { BaixaLinha, CONFIG_CONTA_PAGAR, ContaFinanceiraUiConfig } from 'src/app/util/conta-financeira/conta-financeira.model';
import {
  calcValorAcrescimo,
  calcValorPago,
  calcValorRestante,
  labelStatusPagamento,
  montarBaixasLista,
} from 'src/app/util/conta-financeira/conta-financeira.util';
import { abrirAnexoVisualizar } from 'src/app/util/conta-financeira/anexo-visualizar.util';
import { isContaFinanceiraAberta, parseValorMonetario } from 'src/app/util/conta-financeira/conta-financeira.helpers';
import { formatarDataApi, parseDataApi } from 'src/app/util/date-format.util';

interface Pagador {
  id: number;
  razaoSocial: string;
}

@Component({
  selector: 'app-conta-pagar-form',
  templateUrl: './conta-pagar-form.component.html',
  styleUrls: ['./conta-pagar.component.scss'],
})
export class ContaPagarFormComponent implements OnInit {

  formulario: FormGroup;
  baixaForm: FormGroup;
  mensagemErros: String[] = [];
  bloquearCampoEdicao: boolean;
  myControl = new FormControl('');
  pagadoresLista: Pagador[] = [];
  segmentos: Segmento[] = [];
  favorecidoSelecionado: Pagador = { id: 0, razaoSocial: '' };
  arquivoBaixa: File | null = null;
  nomeArquivoBaixaSelecionado = '';
  arquivoFatura: File | null = null;
  nomeArquivoFaturaSelecionado = '';
  possuiAnexoFatura = false;
  possuiAnexoBaixa = false;
  nomeArquivoFaturaSalvo = '';
  nomeArquivoBaixaSalvo = '';
  config: ContaFinanceiraUiConfig = CONFIG_CONTA_PAGAR;
  valorBaixa: number | null = null;
  valorAcrescimo = 0;
  valorPago = 0;
  valorRestante = 0;
  statusPagamento = 'A PAGAR';
  baixas: BaixaLinha[] = [];
  private situacaoDescricaoAtual = '';

  constructor(
    private contaPagarService: ContaPagarService,
    private formBilder: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private clientService: ClientService,
    private fornecedorService: FornecedorService,
    private colaboradorService: ColaboradorService,
    private segmentoService: SegmentoService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.montarFormulario();
    this.montarBaixaForm();
    this.carregarSegmentos();
    this.myControl = new FormControl('', [Validators.required, this.validateSelection()]);
    this.myControl.valueChanges.subscribe(value => this.handleFavorecidoChange(value));
    this.formulario.get('tipoPagador')?.valueChanges.subscribe(() => this.listarPagador());
    this.formulario.valueChanges.subscribe(() => this.atualizarResumoFinanceiro());
    this.atualizarResumoFinanceiro();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.carregarEdicao(+id);
    }
  }

  voltarConsulta() {
    this.router.navigate(['/contaPagar/consulta']);
  }

  montarFormulario() {
    this.bloquearCampoEdicao = false;
    this.formulario = this.formBilder.group({
      id: [null, Validators.nullValidator],
      valor: [null, [Validators.required]],
      juros: [null, Validators.nullValidator],
      multa: [null, Validators.nullValidator],
      desconto: [null, Validators.nullValidator],
      total: [null, Validators.nullValidator],
      dtVencimento: [null, Validators.required],
      dtPagamento: [null, Validators.nullValidator],
      parcela: ['1', Validators.required],
      pagamento: [null, Validators.nullValidator],
      situacao: ['A_PAGAR'],
      observacao: [null, Validators.nullValidator],
      tipoPagador: [null, Validators.required],
      descricao: [null, Validators.required],
      nrDocumento: [null, Validators.nullValidator],
      codigoBarras: [null, Validators.nullValidator],
      segmentoId: [null],
    });
  }

  carregarSegmentos() {
    this.segmentoService.listarAtivos().subscribe(lista => {
      this.segmentos = lista;
    });
  }

  submit() {
    if (this.formulario.invalid || this.myControl.invalid) {
      this.snackBar.open('Preencha todos os campos obrigatórios.', 'ERRO', { duration: 2000 });
      return;
    }

    const formValues = this.formulario.value;
    const tipo = formValues.tipoPagador;
    let clienteId: number | null = null;
    let fornecedorId: number | null = null;
    let colaboradorId: number | null = null;

    if (!this.favorecidoSelecionado?.id) {
      this.snackBar.open('Selecione o favorecido na lista.', 'ERRO', { duration: 2000 });
      return;
    }
    if (tipo === 'CLIENTE') {
      clienteId = this.favorecidoSelecionado.id;
    } else if (tipo === 'FORNECEDOR') {
      fornecedorId = this.favorecidoSelecionado.id;
    } else if (tipo === 'COLABORADOR') {
      colaboradorId = this.favorecidoSelecionado.id;
    }

    const conta = new ContaPagar(
      formValues.id,
      formValues.valor,
      formValues.juros,
      formValues.multa,
      formValues.desconto,
      formValues.total,
      fornecedorId,
      formatarDataApi(formValues.dtVencimento),
      null,
      formValues.parcela,
      formValues.pagamento,
      formValues.situacao,
      formValues.observacao,
      tipo,
      clienteId,
      formValues.descricao,
      formValues.nrDocumento,
      formValues.codigoBarras,
      null,
      ''
    );
    conta.colaboradorId = colaboradorId;
    conta.segmentoId = formValues.segmentoId ?? null;

    const aoSucesso = (mensagem: string, contaSalva?: ContaPagar) => {
      this.snackBar.open(mensagem, 'Sucesso', { duration: 2000 });
      if (contaSalva?.id) {
        this.formulario.patchValue({ id: contaSalva.id, situacao: contaSalva.situacao ?? 'A_PAGAR' });
        this.bloquearCampoEdicao = true;
        this.baixaForm.patchValue({ valor: contaSalva.total, dataPagamento: new Date() });
        this.atualizarMetadadosAnexos(contaSalva);
        if (this.arquivoFatura) {
          this.contaPagarService.anexarFatura(contaSalva.id, this.arquivoFatura).subscribe((res) => {
            this.atualizarMetadadosAnexos(res);
            this.arquivoFatura = null;
            this.nomeArquivoFaturaSelecionado = '';
          }, () => this.snackBar.open('Conta salva, mas falhou ao anexar fatura.', 'ERRO', { duration: 3000 }));
        }
      } else {
        this.limparFormulario();
      }
    };

    if (formValues.id) {
      this.contaPagarService.update(conta).subscribe(res => {
        aoSucesso('Conta a pagar alterada com sucesso!', res);
      }, errorResponse => {
        this.snackBar.open(errorResponse.error?.message ?? 'Erro ao alterar conta.', 'ERRO', { duration: 2000 });
      });
    } else {
      this.contaPagarService.save(conta).subscribe(res => {
        aoSucesso('Conta a pagar salva com sucesso!', res);
      }, errorResponse => {
        this.snackBar.open(errorResponse.error?.message ?? 'Erro ao salvar conta.', 'ERRO', { duration: 2000 });
      });
    }
  }

  limparFormulario() {
    this.router.navigate(['/contaPagar/form']);
    this.formulario.reset({ parcela: '1', situacao: 'A_PAGAR' });
    this.baixaForm.reset({ dataPagamento: new Date() });
    this.myControl.setValue('');
    this.favorecidoSelecionado = { id: 0, razaoSocial: '' };
    this.bloquearCampoEdicao = false;
    this.valorBaixa = null;
    this.arquivoBaixa = null;
    this.arquivoFatura = null;
    this.nomeArquivoBaixaSelecionado = '';
    this.nomeArquivoFaturaSelecionado = '';
    this.possuiAnexoFatura = false;
    this.possuiAnexoBaixa = false;
    this.situacaoDescricaoAtual = '';
    this.atualizarResumoFinanceiro();
  }

  carregarEdicao(id: number) {
    this.contaPagarService.findClientById(id).subscribe((response) => {
      this.formulario.patchValue({
        id,
        valor: response.valor,
        juros: response.juros,
        multa: response.multa,
        desconto: response.desconto,
        total: response.total,
        dtVencimento: parseDataApi(response.dtVencimento),
        dtPagamento: response.dtPagamento,
        parcela: response.parcela,
        pagamento: response.pagamento,
        situacao: response.situacao,
        observacao: response.observacao,
        descricao: response.descricao,
        nrDocumento: response.nrDocumento,
        codigoBarras: response.codigoBarras,
        tipoPagador: response.favorecido,
        segmentoId: response.segmentoId ?? null,
      });

      const tipo = String(response.favorecido ?? '');
      const razaoSocial = String(response.favorecidoDescricao ?? '');
      const carregarERestaurar = (lista: Pagador[]) => {
        this.pagadoresLista = lista;
        const encontrado = lista.find(p =>
          p.razaoSocial?.toLowerCase().trim() === razaoSocial?.toLowerCase().trim()
        );
        if (encontrado) {
          this.favorecidoSelecionado = encontrado;
          this.myControl.setValue(encontrado, { emitEvent: false });
        } else if (tipo === 'CLIENTE' && response.clienteId) {
          this.favorecidoSelecionado = { id: response.clienteId, razaoSocial };
          this.myControl.setValue(this.favorecidoSelecionado, { emitEvent: false });
        } else if (tipo === 'FORNECEDOR' && response.fornecedorId) {
          this.favorecidoSelecionado = { id: response.fornecedorId, razaoSocial };
          this.myControl.setValue(this.favorecidoSelecionado, { emitEvent: false });
        }
      };

      if (tipo === 'FORNECEDOR') {
        this.fornecedorService.listarFornecedor().subscribe((lista: Pagador[]) => carregarERestaurar(lista));
      } else if (tipo === 'COLABORADOR') {
        this.colaboradorService.listarAtivos().subscribe(lista => carregarERestaurar(this.mapearColaboradores(lista)));
      } else {
        this.clientService.listarCliente().subscribe((lista: Pagador[]) => carregarERestaurar(lista));
      }
      this.atualizarMetadadosAnexos(response);
      this.baixaForm.patchValue({
        valor: response.total,
        dataPagamento: parseDataApi(response.dtPagamento) || new Date(),
      });
    });

    this.bloquearCampoEdicao = true;
  }

  calcularValorTotal() {
    const fv = this.formulario.value;
    const total = (Number(fv.valor) || 0) - (Number(fv.desconto) || 0) + (Number(fv.juros) || 0) + (Number(fv.multa) || 0);
    this.formulario.controls.total.setValue(total, { emitEvent: false });
    this.atualizarResumoFinanceiro();
  }

  atualizarResumoFinanceiro() {
    const fv = this.formulario?.value || {};
    this.valorAcrescimo = calcValorAcrescimo(fv.juros, fv.multa);
    this.valorPago = calcValorPago(fv.situacao, this.config, this.valorBaixa, fv.total);
    this.valorRestante = calcValorRestante(fv.total, this.valorPago);
    this.statusPagamento = labelStatusPagamento(fv.situacao, this.situacaoDescricaoAtual, this.config);
    this.baixas = montarBaixasLista({
      situacao: fv.situacao,
      pagamento: fv.pagamento,
      valorBaixa: this.valorBaixa,
      total: fv.total,
      dtPagamento: fv.dtPagamento,
      possuiAnexoBaixa: this.possuiAnexoBaixa,
      config: this.config,
    });
  }

  listarPagador() {
    const tipoSelecionado = this.formulario.get('tipoPagador')?.value;
    this.pagadoresLista = [];
    this.myControl.setValue('');
    if (tipoSelecionado !== 'CLIENTE') {
      this.formulario.get('segmentoId')?.setValue(null, { emitEvent: false });
    }
    if (tipoSelecionado === 'FORNECEDOR') {
      this.fornecedorService.listarFornecedor().subscribe((response: Pagador[]) => {
        this.pagadoresLista = response;
      });
    } else if (tipoSelecionado === 'CLIENTE') {
      this.clientService.listarCliente().subscribe((response: any[]) => {
        this.pagadoresLista = this.filtrarClientesPorSegmento(response);
      });
    } else if (tipoSelecionado === 'COLABORADOR') {
      this.colaboradorService.listarAtivos().subscribe(lista => {
        this.pagadoresLista = this.mapearColaboradores(lista);
      });
    }
  }

  private mapearColaboradores(lista: any[]): Pagador[] {
    return (lista ?? []).map(c => ({ id: c.id, razaoSocial: c.nome }));
  }

  private filtrarClientesPorSegmento(clientes: any[]): Pagador[] {
    let lista = clientes ?? [];
    const segId = this.formulario.get('segmentoId')?.value;
    const segmento = this.segmentos.find(s => s.id === segId);
    const codigo = segmento?.codigo;
    if (codigo) {
      lista = lista.filter(c => Array.isArray(c.segmentos) && c.segmentos.includes(codigo));
    }
    return lista.map(c => ({ id: c.id, razaoSocial: c.razaoSocial }));
  }

  displayFn(pagador: Pagador | string): string {
    if (pagador && (pagador as Pagador).razaoSocial) {
      return (pagador as Pagador).razaoSocial;
    }
    if (typeof pagador === 'string') {
      return pagador;
    }
    return '';
  }

  handleFavorecidoChange(value: Pagador | string | null): void {
    if (typeof value === 'object' && value !== null && value.id) {
      this.favorecidoSelecionado = value;
    }
  }

  montarBaixaForm() {
    this.baixaForm = this.formBilder.group({
      formaPagamento: [null, Validators.required],
      valor: [null],
      dataPagamento: [new Date(), Validators.required],
    });
  }

  contaAberta(): boolean {
    return isContaFinanceiraAberta(this.formulario?.value?.situacao, 'A_PAGAR');
  }

  onArquivoBaixa(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) { return; }
    this.arquivoBaixa = input.files[0];
    this.nomeArquivoBaixaSelecionado = this.arquivoBaixa.name;
    input.value = '';
  }

  onArquivoFatura(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) { return; }
    this.arquivoFatura = input.files[0];
    this.nomeArquivoFaturaSelecionado = this.arquivoFatura.name;
    input.value = '';
  }

  registrarBaixa() {
    const id = this.formulario.value.id;
    if (!id) {
      this.snackBar.open('Salve a conta antes de registrar a baixa.', 'ERRO', { duration: 2000 });
      return;
    }
    if (this.baixaForm.invalid) {
      this.snackBar.open('Informe a forma e a data do pagamento.', 'ERRO', { duration: 2000 });
      return;
    }
    if (!this.contaAberta()) {
      this.snackBar.open('Esta conta já está paga.', 'ERRO', { duration: 2000 });
      return;
    }
    const b = this.baixaForm.value;
    const valor = parseValorMonetario(b.valor) ?? parseValorMonetario(this.formulario.value.total);
    this.contaPagarService.registrarBaixa(
      id, b.formaPagamento, valor, formatarDataApi(b.dataPagamento), this.arquivoBaixa
    ).subscribe(res => {
      this.snackBar.open('Baixa registrada. Situação atualizada para Pago.', 'Sucesso', { duration: 2500 });
      this.formulario.patchValue({ situacao: res.situacao, dtPagamento: res.dtPagamento, pagamento: res.pagamento });
      this.valorBaixa = res.valorBaixa ?? res.total;
      this.atualizarMetadadosAnexos(res);
      this.atualizarResumoFinanceiro();
      this.arquivoBaixa = null;
      this.nomeArquivoBaixaSelecionado = '';
    }, err => this.snackBar.open(err.error?.message ?? 'Erro na baixa.', 'ERRO', { duration: 3000 }));
  }

  anexarFaturaConta() {
    const id = this.formulario.value.id;
    if (!id || !this.arquivoFatura) {
      this.snackBar.open('Selecione a fatura e salve/edite a conta antes.', 'ERRO', { duration: 2000 });
      return;
    }
    this.contaPagarService.anexarFatura(id, this.arquivoFatura).subscribe(res => {
      this.snackBar.open('Fatura anexada!', 'Sucesso', { duration: 2000 });
      this.atualizarMetadadosAnexos(res);
      this.arquivoFatura = null;
      this.nomeArquivoFaturaSelecionado = '';
    }, err => this.snackBar.open(err.error?.message ?? 'Erro ao anexar fatura.', 'ERRO', { duration: 3000 }));
  }

  baixarAnexo(tipo: 'fatura' | 'baixa') {
    const id = this.formulario.value.id;
    if (!id) { return; }
    this.contaPagarService.downloadAnexo(id, tipo).subscribe(blob => {
      const nome = tipo === 'fatura' ? this.nomeArquivoFaturaSalvo : this.nomeArquivoBaixaSalvo;
      abrirAnexoVisualizar(this.dialog, blob, nome || (tipo === 'fatura' ? 'fatura.pdf' : 'comprovante.pdf'));
    });
  }

  atualizarMetadadosAnexos(conta: ContaPagar) {
    this.possuiAnexoFatura = !!conta.possuiAnexoFatura;
    this.possuiAnexoBaixa = !!conta.possuiAnexoBaixa;
    this.nomeArquivoFaturaSalvo = conta.nomeArquivoFatura || '';
    this.nomeArquivoBaixaSalvo = conta.nomeArquivoBaixa || '';
    this.valorBaixa = conta.valorBaixa ?? null;
    this.situacaoDescricaoAtual = conta.situacaoDescricao || '';
    this.statusPagamento = labelStatusPagamento(conta.situacao, this.situacaoDescricaoAtual, this.config);
    this.atualizarResumoFinanceiro();
  }

  private validateSelection(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
      const value = control.value;
      if (value === null || value === '') {
        return null;
      }
      const isObject = typeof value === 'object' && value !== null && value.id;
      return isObject ? null : { invalidSelection: true };
    };
  }
}
